// src/utils/referralCard.ts
// ============================================================================
// RENDERIZADOR DE LA TARJETA DE INVITACIÓN (referidos) — canvas, sin deps
// ============================================================================
//
// Compone una tarjeta compartible (imagen) sobre un fondo generado con IA
// (Gemini "Nano Banana Pro", horneado a public/referral-cards/) y sobrepone
// el TEXTO crítico (negocio, código, recompensa, instrucciones) por canvas.
//
// ⚠️ El texto NUNCA va en la imagen de IA (nano banana deforma códigos/palabras
// largas en español). Por eso es HÍBRIDO: ilustración IA + texto nítido por canvas.
// ============================================================================

export type ReferralCardVariant = 'amigos' | 'elegante';

export interface ReferralCardOptions {
  businessName: string;
  /** Código de influencer (ej. "DOME100"). Si no hay, se usa el teléfono. */
  code?: string | null;
  /** Teléfono del referidor (fallback si no hay código). Solo dígitos. */
  phone?: string | null;
  /** Etiqueta de recompensa ya formateada, ej. "10 puntos" o "$50". Opcional. */
  rewardLabel?: string | null;
  /** URL del logo del negocio (opcional; se omite si no carga / taints). */
  logoUrl?: string | null;
  /** Color primario de marca (#hex) para acentos. Default emerald. */
  primaryColor?: string | null;
  variant?: ReferralCardVariant;
}

const W = 1080;
const H = 1350;

function loadImage(src: string, crossOrigin?: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    if (crossOrigin) img.crossOrigin = crossOrigin;
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

// Envuelve texto a un ancho máximo, devolviendo líneas.
function wrapLines(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const words = text.split(' ');
  const lines: string[] = [];
  let line = '';
  for (const word of words) {
    const test = line ? `${line} ${word}` : word;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = word;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  return lines;
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/**
 * Renderiza la tarjeta y devuelve un Blob PNG listo para compartir/descargar.
 */
export async function renderReferralCard(opts: ReferralCardOptions): Promise<Blob> {
  const {
    businessName,
    code,
    phone,
    rewardLabel,
    logoUrl,
    primaryColor = '#059669',
    variant = 'amigos',
  } = opts;

  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('No se pudo crear el canvas');

  // ── Fondo IA ──
  try {
    const bg = await loadImage(`/referral-cards/bg-${variant}.jpg`);
    ctx.drawImage(bg, 0, 0, W, H);
  } catch {
    // Fallback: gradiente sólido si el asset no carga
    const g = ctx.createLinearGradient(0, 0, 0, H);
    g.addColorStop(0, '#065f46');
    g.addColorStop(1, '#0e7490');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);
  }

  // ── Scrim inferior para legibilidad del texto ──
  const scrim = ctx.createLinearGradient(0, H * 0.42, 0, H);
  scrim.addColorStop(0, 'rgba(8,30,24,0)');
  scrim.addColorStop(0.5, 'rgba(8,30,24,0.55)');
  scrim.addColorStop(1, 'rgba(8,30,24,0.88)');
  ctx.fillStyle = scrim;
  ctx.fillRect(0, H * 0.42, W, H * 0.58);

  ctx.textAlign = 'center';
  let y = 640;

  // ── Logo del negocio (opcional, circular) ──
  if (logoUrl) {
    try {
      const logo = await loadImage(logoUrl, 'anonymous');
      const size = 130;
      const lx = W / 2 - size / 2;
      ctx.save();
      ctx.beginPath();
      ctx.arc(W / 2, y, size / 2, 0, Math.PI * 2);
      ctx.closePath();
      ctx.fillStyle = '#ffffff';
      ctx.fill();
      ctx.clip();
      ctx.drawImage(logo, lx, y - size / 2, size, size);
      ctx.restore();
      y += size / 2 + 50;
    } catch {
      // Si el logo no carga o taint-ea el canvas, lo omitimos sin romper.
    }
  }

  // ── Encabezado ──
  ctx.fillStyle = 'rgba(255,255,255,0.85)';
  ctx.font = '600 34px Outfit, system-ui, sans-serif';
  ctx.fillText('TE RECOMIENDO', W / 2, y);
  y += 70;

  // ── Nombre del negocio ──
  ctx.fillStyle = '#ffffff';
  ctx.font = '800 68px Outfit, system-ui, sans-serif';
  for (const line of wrapLines(ctx, businessName, W - 160)) {
    ctx.fillText(line, W / 2, y);
    y += 78;
  }
  y += 24;

  // ── Pill del código / teléfono ──
  const hasCode = !!(code && code.trim());
  const codeText = hasCode ? code!.trim().toUpperCase() : (phone ? phone.replace(/\D/g, '') : '');
  if (codeText) {
    const label = hasCode ? 'Menciona el código' : 'Menciona el número';
    ctx.fillStyle = 'rgba(255,255,255,0.85)';
    ctx.font = '500 30px Outfit, system-ui, sans-serif';
    ctx.fillText(label, W / 2, y);
    y += 30;

    ctx.font = '800 64px "Outfit", system-ui, sans-serif';
    const tw = ctx.measureText(codeText).width;
    const padX = 54;
    const pillW = Math.min(W - 120, tw + padX * 2);
    const pillH = 110;
    const px = W / 2 - pillW / 2;
    roundRect(ctx, px, y, pillW, pillH, pillH / 2);
    ctx.fillStyle = '#ffffff';
    ctx.fill();
    ctx.fillStyle = primaryColor || '#059669';
    ctx.textBaseline = 'middle';
    ctx.fillText(codeText, W / 2, y + pillH / 2 + 4);
    ctx.textBaseline = 'alphabetic';
    y += pillH + 56;
  }

  // ── Instrucciones ──
  ctx.fillStyle = 'rgba(255,255,255,0.92)';
  ctx.font = '400 34px Outfit, system-ui, sans-serif';
  const instr = 'Regístrate en la tienda y menciónalo al dar tu número de teléfono.';
  for (const line of wrapLines(ctx, instr, W - 200)) {
    ctx.fillText(line, W / 2, y);
    y += 46;
  }
  y += 24;

  // ── Recompensa ──
  if (rewardLabel) {
    ctx.fillStyle = primaryColor || '#10b981';
    ctx.font = '800 40px Outfit, system-ui, sans-serif';
    ctx.fillText(`🎁 Ambos ganamos ${rewardLabel}`, W / 2, y);
  }

  // ── Pie ──
  ctx.fillStyle = 'rgba(255,255,255,0.6)';
  ctx.font = '500 26px Outfit, system-ui, sans-serif';
  ctx.fillText('Programa de lealtad', W / 2, H - 48);

  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('No se pudo generar la imagen'));
    }, 'image/png');
  });
}
